// Anthropic SDK loader. Lazy dynamic import so the ~50KB-gzip SDK only
// downloads once the creator actually starts a chat turn — not on first
// paint. Constructor promise cached; client cached per key.
//
// BYOK, browser-direct (dangerouslyAllowBrowser): the creator's key
// stays in their browser and talks straight to Anthropic. No BKA server
// in the path — same posture as everything else here.
//
// Copied from agicore-foundry's anthropic-client.ts; default model
// bumped to the current Sonnet.

import type Anthropic from "@anthropic-ai/sdk";

let ctorPromise: Promise<typeof Anthropic> | null = null;
let cached: Anthropic | null = null;
let cachedKey: string | null = null;

function loadAnthropic(): Promise<typeof Anthropic> {
  if (!ctorPromise) {
    ctorPromise = import("@anthropic-ai/sdk").then((m) => m.default);
  }
  return ctorPromise;
}

export async function getClient(apiKey: string): Promise<Anthropic> {
  if (cached && cachedKey === apiKey) return cached;
  const Ctor = await loadAnthropic();
  cached = new Ctor({ apiKey, dangerouslyAllowBrowser: true });
  cachedKey = apiKey;
  return cached;
}

export interface ModelChoice {
  id: string;
  label: string;
  blurb: string;
}

// The composer is a writing tool; Sonnet is the sensible default
// (fast + strong prose, cheaper than Opus for iterative drafting). The
// creator pays Anthropic directly, so we expose the trade-off.
export const MODELS: ModelChoice[] = [
  { id: "claude-sonnet-4-6", label: "Sonnet 4.6", blurb: "fast, strong writing — the default" },
  { id: "claude-opus-4-8", label: "Opus 4.8", blurb: "most capable, slower + pricier" },
  { id: "claude-haiku-4-5-20251001", label: "Haiku 4.5", blurb: "quickest + cheapest" },
];

export const DEFAULT_MODEL = "claude-sonnet-4-6";
